import {css} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {MOLECULE_CHEM_SEARCH_MODAL_TITLE} from 'chemistry/ChemistryConstants';
import ChemSearchModalFrame from 'chemistry/ChemSearchModalFrame';
import {ChemSearchMoleculeOptions, ChemSearchTypeToggle} from 'chemistry/ChemSearchModalOptions';
import {chemSearchModalStyles} from 'chemistry/ChemSearchModalStyles';
import {buildMoleculeChemSearchSubmitPayload, ChemSearchSubmit} from 'chemistry/chemSearchSubmit';
import {
  getDefaultChemSearchOperator,
  getDefaultMinSimilarity,
  ToggleHighlightParams,
} from 'chemistry/ChemSearchUtils';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {useChemSearchModalSubmit} from 'chemistry/useChemSearchModalSubmit';
import {ChemicalStructureSearchOperator, SearchParams} from 'search/SearchConstants';

export interface MoleculeChemSearchModalProps {
  initialMol: string | null;
  onRequestSubmit: (submit: ChemSearchSubmit) => void;
  onRequestClose: () => void;
  toggleHighlightParams: ToggleHighlightParams;
  searchParams: SearchParams;
}

export default function MoleculeChemSearchModal({
  initialMol,
  onRequestSubmit,
  onRequestClose,
  toggleHighlightParams,
  searchParams,
}: MoleculeChemSearchModalProps): JSX.Element {
  const [mol, setMol] = useState<ChemicalStructureDrawerValue>(initialMol);
  const [highlightChecked, setHighlightChecked] = useState<boolean>(
    toggleHighlightParams.shouldHighlightSubstructure
  );
  const [chemSearchOperator, setChemSearchOperator] = useState<ChemicalStructureSearchOperator>(
    getDefaultChemSearchOperator(searchParams)
  );
  const [minSimilarity, setMinSimilarity] = useState<number>(getDefaultMinSimilarity(searchParams));

  const buildSubmitResult = useCallback(
    () =>
      buildMoleculeChemSearchSubmitPayload({
        mol,
        chemSearchOperator,
        minSimilarity,
      }),
    [chemSearchOperator, minSimilarity, mol]
  );

  const {submitError, clearSubmitError, handleSubmit} = useChemSearchModalSubmit({
    toggleHighlightParams,
    highlightChecked,
    chemSearchOperator,
    onRequestSubmit,
    buildSubmitResult,
  });

  return (
    <ChemSearchModalFrame
      title={MOLECULE_CHEM_SEARCH_MODAL_TITLE}
      submitError={submitError}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <div className={css(chemSearchModalStyles.chemicalDrawer)}>
        <ChemicalStructureDrawer
          onMoleculeChange={(chemicalStructureQuery) => {
            clearSubmitError();
            setMol(chemicalStructureQuery);
          }}
          initialValue={mol}
          mode={ChemicalStructureDrawerMode.STANDARD}
        />
      </div>
      <div className={css(chemSearchModalStyles.searchOptionsContainer)}>
        <div className={css(chemSearchModalStyles.searchOptions)}>
          <ChemSearchTypeToggle
            chemSearchOperator={chemSearchOperator}
            setChemSearchOperator={(operator) => {
              clearSubmitError();
              setChemSearchOperator(operator);
            }}
          />
          <ChemSearchMoleculeOptions
            highlightChecked={highlightChecked}
            setHighlightChecked={setHighlightChecked}
            chemSearchOperator={chemSearchOperator}
            minSimilarity={minSimilarity}
            setMinSimilarity={(similarity) => {
              clearSubmitError();
              setMinSimilarity(similarity);
            }}
            toggleHighlightParams={toggleHighlightParams}
          />
        </div>
      </div>
    </ChemSearchModalFrame>
  );
}
